import { useEffect, useCallback, useRef, useLayoutEffect } from 'react';

export interface UsePopupHistoryOptions {
  /** Whether the popup/modal is open */
  isOpen: boolean;
  /** Called when the user presses browser back (popstate) */
  onClose: () => void;
  /** Unique key for this popup's history state (default: 'popupOpen') */
  stateKey?: string;
}

/**
 * Hook for popups/modals that should respond to the browser back button.
 * Pushes a history entry when the popup opens so that back closes it,
 * and listens for popstate to call onClose.
 *
 * @example
 * const { goBack } = usePopupHistory({
 *   isOpen,
 *   onClose,
 *   stateKey: 'searchModal',
 * });
 * // Use goBack() for close/back buttons so URL and modal stay in sync
 */
export function usePopupHistory({
  isOpen,
  onClose,
  stateKey = 'popupOpen',
}: UsePopupHistoryOptions): { goBack: () => void } {
  // Keep a stable ref to onClose so re-renders of the parent that recreate the
  // callback (e.g. inline arrow functions) do not re-trigger the effect and push
  // extra history entries.
  const onCloseRef = useRef(onClose);
  // Update ref after each render (before effects fire) — avoids the
  // react-hooks/refs lint rule that forbids direct render-time mutation.
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;

    const url = window.location.href;
    window.history.pushState({ [stateKey]: true }, '', url);

    const handlePopState = () => {
      onCloseRef.current();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, stateKey]); // onClose intentionally excluded — read via ref

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  return { goBack };
}
